/*
----------------------------------------------------------------------------
AI agent prompt (hero banner + persistent prompt bar)
----------------------------------------------------------------------------
Usage:
import AiPrompt from './modules/ai-prompt.js'
_('ai-prompt').nodes().forEach((el) => new AiPrompt(el))

The hero field and the bar are one logical input shown in two positions:
value, validation and disabled state are shared, and only the visible
position is exposed to assistive technology.
Submitting dispatches `ai-agent:open` for the chat module (ai-agent.js).
*/

import { _ } from '../utils.js'

export const AI_AGENT_OPEN = 'ai-agent:open'
export const AI_AGENT_STATE = 'ai-agent:state'
export const AI_AGENT_AVAILABILITY = 'ai-agent:availability'

export default class AiPrompt {
    constructor(el) {
        this.el = el
        this.maxLength = parseInt(el.dataset.maxLength, 10) || 4000
        this.forms = _('ai-prompt-form', 'element').nodesFrom(el)
        this.heroForm = this.forms.find((form) => form.dataset.position === 'hero')
        this.bar = _('ai-prompt-bar', 'element').nodeFrom(el)
        this.barForm = this.bar ? _('ai-prompt-form', 'element').nodeFrom(this.bar) : null
        this.announcer = _('ai-prompt-announcer', 'element').nodeFrom(el)
        this.value = ''
        this.available = true
        this.chatOpen = false
        this.heroVisible = true

        this.forms.forEach((form) => {
            const input = this.input(form)
            input.addEventListener('input', () => this.setValue(input.value, input))
            form.addEventListener('submit', (event) => {
                event.preventDefault()
                this.submit()
            })
        })

        _('ai-prompt-question', 'element').nodesFrom(el).forEach((button) => {
            button.addEventListener('click', () => {
                this.setValue(button.textContent.trim())
                const input = this.input(this.heroForm)
                input.focus()
                input.setSelectionRange(input.value.length, input.value.length)
            })
        })

        document.addEventListener(AI_AGENT_STATE, (event) => {
            this.chatOpen = Boolean(event.detail && event.detail.open)
            this.updateBar()
        })
        document.addEventListener(AI_AGENT_AVAILABILITY, (event) => {
            this.available = Boolean(event.detail && event.detail.available)
            this.render()
        })

        this.observeHero()
        this.render()
    }

    input = (form) => _('ai-prompt-input', 'element').nodeFrom(form)

    setValue = (value, source = null) => {
        this.value = value.slice(0, this.maxLength)
        this.forms.forEach((form) => {
            const input = this.input(form)
            if (input !== source) {
                input.value = this.value
            }
        })
        this.render()
    }

    render = () => {
        const trimmed = this.value.trim()
        const atLimit = this.value.length >= this.maxLength

        this.forms.forEach((form) => {
            const input = this.input(form)
            const submit = _('ai-prompt-submit', 'element').nodeFrom(form)
            const limit = _('ai-prompt-limit', 'element').nodeFrom(form)
            const unavailable = _('ai-prompt-unavailable', 'element').nodeFrom(form)

            input.disabled = !this.available
            submit.disabled = !this.available || trimmed === ''
            if (limit) {
                limit.hidden = !atLimit
            }
            if (unavailable) {
                unavailable.hidden = this.available
            }
        })
    }

    submit = () => {
        const message = this.value.trim()
        if (!message || !this.available) {
            return
        }
        document.dispatchEvent(new CustomEvent(AI_AGENT_OPEN, { detail: { message } }))
        this.setValue('')
    }

    observeHero = () => {
        if (!this.bar || !this.heroForm || !('IntersectionObserver' in window)) {
            return
        }
        // The huge bottom margin means "intersecting" is simply "not yet scrolled off the top",
        // so a jump from below the viewport to above it still flips state.
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                this.heroVisible = entry.isIntersecting
                this.updateBar()
            })
        }, { rootMargin: '0px 0px 100000px 0px' })
        observer.observe(this.heroForm)
    }

    updateBar = () => {
        if (!this.bar) {
            return
        }
        const showBar = !this.heroVisible && !this.chatOpen
        if (showBar === !this.bar.hidden) {
            return
        }

        const active = document.activeElement
        const barHadFocus = this.bar.contains(active)
        const heroHadFocus = this.heroForm.contains(active)

        this.bar.hidden = !showBar
        this.heroForm.inert = showBar
        document.body.classList.toggle('has-ai-prompt-bar', showBar)

        if (showBar) {
            this.announcer.textContent = 'The AI agent prompt is now available at the bottom of the screen.'
            if (heroHadFocus) {
                this.input(this.barForm).focus({ preventScroll: true })
            }
        } else {
            this.announcer.textContent = ''
            if (barHadFocus) {
                this.input(this.heroForm).focus({ preventScroll: true })
            }
        }
    }
}

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

export const scrollToTarget = (target) => {
    if (!target) {
        return
    }
    const header = document.querySelector('.header')
    const offset = header ? header.getBoundingClientRect().height : 0
    const top = target.getBoundingClientRect().top + window.scrollY - offset - 24
    window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' })

    const focusTarget = target.querySelector('[tabindex="-1"]') || target
    focusTarget.focus({ preventScroll: true })
}

export const bindAgentTriggers = () => {
    _('ai-agent-open').nodes().forEach((button) => {
        button.addEventListener('click', () => {
            document.dispatchEvent(new CustomEvent(AI_AGENT_OPEN, { detail: { message: '' } }))
        })
    })

    _('ai-scroll-to').nodes().forEach((link) => {
        link.addEventListener('click', (event) => {
            const target = document.querySelector(link.getAttribute('href'))
            if (!target) {
                return
            }
            event.preventDefault()
            scrollToTarget(target)
        })
    })
}
